import catalogSnapshot from "./mard-catalog.generated.json";

export const MARD_SERIES = ["A", "B", "C", "D", "E", "F", "G", "H", "M", "P", "Q", "R", "T", "Y", "ZG"] as const;
export type MardSeries = typeof MARD_SERIES[number];

export interface MardCatalogEntry {
  catalogCode: string;
  displayCode: string;
  series: MardSeries;
  hex: `#${string}`;
  rgb: [number, number, number];
  lab: [number, number, number];
}

export interface MardCatalogMetadata {
  sourceUrl: string;
  sourceTitle: string;
  retrievedAt: string;
  catalogVersion: string;
  count: number;
  sha256: string;
}

export const MARD_CATALOG_METADATA = catalogSnapshot.metadata as MardCatalogMetadata;
export const MARD_CATALOG = catalogSnapshot.entries as MardCatalogEntry[];

const catalogByCode = new Map(MARD_CATALOG.map((entry) => [entry.catalogCode, entry]));

export function normalizeMardCatalogCode(value: string): string | null {
  const match = value.trim().toUpperCase().match(/^([A-Z]{1,2})0*(\d{1,2})$/);
  if (!match) return null;
  const normalized = `${match[1]}${Number(match[2])}`;
  return catalogByCode.has(normalized) ? normalized : null;
}

export function findMardColor(value: string): MardCatalogEntry | undefined {
  const normalized = normalizeMardCatalogCode(value);
  return normalized ? catalogByCode.get(normalized) : undefined;
}

export function filterMardColors(query: string, series: MardSeries | "all" = "all"): MardCatalogEntry[] {
  const normalizedQuery = query.trim().toUpperCase().replace(/\s+/g, "");
  const withoutHash = normalizedQuery.replace(/^#/, "");
  return MARD_CATALOG.filter((entry) => {
    if (series !== "all" && entry.series !== series) return false;
    if (!withoutHash) return true;
    return entry.catalogCode.includes(withoutHash)
      || entry.displayCode.includes(withoutHash)
      || entry.hex.slice(1).includes(withoutHash);
  });
}

export function rgbToLab([red, green, blue]: [number, number, number]): [number, number, number] {
  const linear = [red, green, blue].map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const x = ((linear[0] ?? 0) * 0.4124564 + (linear[1] ?? 0) * 0.3575761 + (linear[2] ?? 0) * 0.1804375) / 0.95047;
  const y = (linear[0] ?? 0) * 0.2126729 + (linear[1] ?? 0) * 0.7151522 + (linear[2] ?? 0) * 0.0721750;
  const z = ((linear[0] ?? 0) * 0.0193339 + (linear[1] ?? 0) * 0.1191920 + (linear[2] ?? 0) * 0.9503041) / 1.08883;
  const transform = (value: number) => value > 216 / 24389 ? Math.cbrt(value) : (24389 / 27 * value + 16) / 116;
  const fx = transform(x), fy = transform(y), fz = transform(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

const degrees = (radians: number) => radians * 180 / Math.PI;
const radians = (degreesValue: number) => degreesValue * Math.PI / 180;

/** CIEDE2000 color difference using D65 Lab values. */
export function deltaE2000(left: [number, number, number], right: [number, number, number]): number {
  const [l1, a1, b1] = left;
  const [l2, a2, b2] = right;
  const c1 = Math.hypot(a1, b1), c2 = Math.hypot(a2, b2);
  const averageC = (c1 + c2) / 2;
  const g = 0.5 * (1 - Math.sqrt(averageC ** 7 / (averageC ** 7 + 25 ** 7)));
  const a1Prime = (1 + g) * a1, a2Prime = (1 + g) * a2;
  const c1Prime = Math.hypot(a1Prime, b1), c2Prime = Math.hypot(a2Prime, b2);
  const hue = (a: number, b: number) => {
    const result = degrees(Math.atan2(b, a));
    return result >= 0 ? result : result + 360;
  };
  const h1Prime = hue(a1Prime, b1), h2Prime = hue(a2Prime, b2);
  const deltaLPrime = l2 - l1;
  const deltaCPrime = c2Prime - c1Prime;
  const hueDifference = c1Prime * c2Prime === 0 ? 0
    : Math.abs(h2Prime - h1Prime) <= 180 ? h2Prime - h1Prime
      : h2Prime - h1Prime > 180 ? h2Prime - h1Prime - 360 : h2Prime - h1Prime + 360;
  const deltaHPrime = 2 * Math.sqrt(c1Prime * c2Prime) * Math.sin(radians(hueDifference / 2));
  const averageLPrime = (l1 + l2) / 2;
  const averageCPrime = (c1Prime + c2Prime) / 2;
  const averageHPrime = c1Prime * c2Prime === 0 ? h1Prime + h2Prime
    : Math.abs(h1Prime - h2Prime) <= 180 ? (h1Prime + h2Prime) / 2
      : h1Prime + h2Prime < 360 ? (h1Prime + h2Prime + 360) / 2 : (h1Prime + h2Prime - 360) / 2;
  const t = 1
    - 0.17 * Math.cos(radians(averageHPrime - 30))
    + 0.24 * Math.cos(radians(2 * averageHPrime))
    + 0.32 * Math.cos(radians(3 * averageHPrime + 6))
    - 0.20 * Math.cos(radians(4 * averageHPrime - 63));
  const deltaTheta = 30 * Math.exp(-(((averageHPrime - 275) / 25) ** 2));
  const rC = 2 * Math.sqrt(averageCPrime ** 7 / (averageCPrime ** 7 + 25 ** 7));
  const sL = 1 + 0.015 * (averageLPrime - 50) ** 2 / Math.sqrt(20 + (averageLPrime - 50) ** 2);
  const sC = 1 + 0.045 * averageCPrime;
  const sH = 1 + 0.015 * averageCPrime * t;
  const rT = -Math.sin(radians(2 * deltaTheta)) * rC;
  const lightness = deltaLPrime / sL;
  const chroma = deltaCPrime / sC;
  const hueTerm = deltaHPrime / sH;
  return Math.sqrt(lightness ** 2 + chroma ** 2 + hueTerm ** 2 + rT * chroma * hueTerm);
}

export function nearestMardColors(rgb: [number, number, number], limit = 8): MardCatalogEntry[] {
  const lab = rgbToLab(rgb);
  return MARD_CATALOG
    .map((entry) => ({ entry, difference: deltaE2000(lab, entry.lab) }))
    .sort((left, right) => left.difference - right.difference || left.entry.catalogCode.localeCompare(right.entry.catalogCode))
    .slice(0, Math.max(0, limit))
    .map(({ entry }) => entry);
}
